import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, useFetch, StatusPill, Modal, Field, Select, TextInput, Textarea, PrimaryButton, SearchBar, Pagination } from "../../components/crm/ui";
import { Wrench, Clock, AlertTriangle, ChevronRight, CheckCircle2, Circle, Loader2, Filter, ChevronDown, ChevronUp, CalendarDays } from "lucide-react";
import { TICKET_TYPE_LABEL, ticketCompletionPct, ticketActivityCounts, isTicketInProblem, ageDays } from "../../lib/ticketUtils";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "resolution_support_required", label: "Resolution Support Required" },
  { value: "completed", label: "Completed" },
];

const STATUS_TONE = {
  open: "info",
  in_progress: "warning",
  on_hold: "muted",
  resolution_support_required: "danger",
  completed: "success",
  closed: "success",
};

const PRIORITY_OPTIONS = ["normal", "low", "medium", "high", "critical", "urgent"];

function timeLeft(deadlineISO) {
  if (!deadlineISO) return { text: "—", overdue: false, urgent: false };
  const now = Date.now();
  const d = new Date(deadlineISO).getTime();
  const diff = d - now;
  const overdue = diff < 0;
  const hours = Math.abs(diff) / 3600000;
  if (overdue) {
    if (hours < 24) return { text: `${hours.toFixed(1)}h overdue`, overdue: true, urgent: true };
    return { text: `${(hours / 24).toFixed(1)}d overdue`, overdue: true, urgent: true };
  }
  if (hours < 24) return { text: `${hours.toFixed(1)}h left`, overdue: false, urgent: hours < 6 };
  return { text: `${(hours / 24).toFixed(1)}d left`, overdue: false, urgent: false };
}

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2.5 py-1.5 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[16px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">{label}</div>
      {children}
    </div>
  );
}

function CompletionBar({ pct, done, total }) {
  const color = pct >= 100 ? "bg-[#1F4E2C]" : pct >= 50 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="w-[88px]" title={total ? `${done}/${total} sub-activities done` : "No sub-activities"}>
      <div className="h-1.5 bg-[#EEF2EA] overflow-hidden">
        <div className={"h-full " + color} style={{ width: `${pct}%` }} />
      </div>
      <div className="font-mono text-[10px] text-[#475569] mt-0.5">{pct}%{total > 0 ? ` · ${done}/${total}` : ""}</div>
    </div>
  );
}

export default function ServiceTeamTickets() {
  const { data: tickets, reload } = useFetch("/service-tickets");
  const { data: technicians } = useFetch("/technicians");
  const [detailTicket, setDetailTicket] = useState(null);

  const [q, setQ] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [fStatus, setFStatus] = useState("all");
  const [fType, setFType] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fTech, setFTech] = useState("all");
  const [fBreached, setFBreached] = useState("all");
  const [fProblem, setFProblem] = useState("all");
  const [fDateFrom, setFDateFrom] = useState("");
  const [fDateTo, setFDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Drill-down from the Service Dashboard: seed filters from the URL once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qStatus = params.get("status");
    const qType = params.get("type");
    const qBreached = params.get("breached");
    const qProblem = params.get("problem");
    const qTech = params.get("technician");
    if (qStatus) { setFStatus(qStatus); setFilterOpen(true); }
    if (qType) { setFType(qType); setFilterOpen(true); }
    if (qBreached) { setFBreached(qBreached); setFilterOpen(true); }
    if (qProblem) { setFProblem(qProblem); setFilterOpen(true); }
    if (qTech) { setFTech(qTech); setFilterOpen(true); }
  }, []);

  const techName = useMemo(() => Object.fromEntries(technicians.map((t) => [t.id, t.name])), [technicians]);

  // Re-tick SLA badge every minute
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(t); }, []);

  const activeFilters = useMemo(
    () => [fStatus !== "all", fType !== "all", fPriority !== "all", fTech !== "all", fBreached !== "all", fProblem !== "all", fDateFrom, fDateTo].filter(Boolean).length,
    [fStatus, fType, fPriority, fTech, fBreached, fProblem, fDateFrom, fDateTo]
  );

  function clearFilters() {
    setFStatus("all"); setFType("all"); setFPriority("all"); setFTech("all"); setFBreached("all"); setFProblem("all");
    setFDateFrom(""); setFDateTo(""); setQ(""); setPage(1);
  }

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return tickets.filter((t) => {
      if (fStatus !== "all" && t.status !== fStatus) return false;
      if (fType !== "all" && t.type !== fType) return false;
      if (fPriority !== "all" && (t.priority || "normal") !== fPriority) return false;
      if (fTech !== "all" && (t.technician_id || "") !== fTech) return false;
      if (fBreached !== "all" && String(!!t.sla_breached) !== fBreached) return false;
      if (fProblem !== "all" && String(isTicketInProblem(t)) !== fProblem) return false;
      if (fDateFrom && t.created_at && t.created_at < fDateFrom) return false;
      if (fDateTo && t.created_at && t.created_at > `${fDateTo}T23:59:59`) return false;
      if (query) {
        const hay = `${t.ticket_number || ""} ${t.customer_snapshot?.customer_name || ""} ${t.title || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [tickets, fStatus, fType, fPriority, fTech, fBreached, fProblem, fDateFrom, fDateTo, q]);

  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const kpis = useMemo(() => {
    let open = 0, problem = 0, completed = 0, completionSum = 0;
    for (const t of filtered) {
      if (!["completed", "closed"].includes(t.status)) open++;
      if (isTicketInProblem(t)) problem++;
      if (["completed", "closed"].includes(t.status)) completed++;
      completionSum += ticketCompletionPct(t);
    }
    return {
      total: filtered.length, open, problem, completed,
      avgCompletion: filtered.length ? Math.round(completionSum / filtered.length) : 0,
    };
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="service-tickets-page">
      <PageHeader overline="Field Operations" title="Service Tickets" />

      {/* KPI strip */}
      <div className="flex items-center gap-1 flex-wrap">
        <KpiChip label="Tickets" value={kpis.total} tone="bg-[#1F4E2C] text-white" />
        <KpiChip label="Open" value={kpis.open} tone="bg-sky-50 text-sky-800" />
        <KpiChip label="In Problem" value={kpis.problem} tone="bg-red-50 text-red-700" />
        <KpiChip label="Completed" value={kpis.completed} tone="bg-emerald-50 text-emerald-800" />
        <KpiChip label="Avg Completion" value={`${kpis.avgCompletion}%`} />
      </div>

      {/* Search + filter row */}
      <div className="flex items-center gap-2">
        <div className="flex-1 max-w-md">
          <SearchBar value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search ticket #, customer, title…" />
        </div>
        <button
          onClick={() => setFilterOpen((v) => !v)}
          data-testid="tickets-filter-toggle"
          className={"px-2.5 py-1.5 border font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 " + (filterOpen ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#0F1A12] border-[#DDE5D8] hover:border-[#1F4E2C]")}
        >
          <Filter className="w-3.5 h-3.5" /> Filters{activeFilters > 0 && ` · ${activeFilters}`}
          {filterOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters} data-testid="tickets-filter-clear" className="text-[10.5px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] px-2">Reset</button>
        )}
        <div className="font-mono text-[10.5px] text-[#475569]">
          <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {tickets.length}
        </div>
      </div>

      {filterOpen && (
        <div className="border border-[#DDE5D8] bg-[#FFFDF8] p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="tickets-filter-panel">
          <FilterField label="Status">
            <Select value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(1); }} data-testid="filter-status">
              <option value="all">— All —</option>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              <option value="closed">Closed</option>
            </Select>
          </FilterField>
          <FilterField label="Type">
            <Select value={fType} onChange={(e) => { setFType(e.target.value); setPage(1); }} data-testid="filter-type">
              <option value="all">— All —</option>
              {Object.entries(TICKET_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Priority">
            <Select value={fPriority} onChange={(e) => { setFPriority(e.target.value); setPage(1); }} data-testid="filter-priority">
              <option value="all">— All —</option>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Technician">
            <Select value={fTech} onChange={(e) => { setFTech(e.target.value); setPage(1); }} data-testid="filter-technician">
              <option value="all">— All —</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </FilterField>
          <FilterField label="SLA Breached">
            <Select value={fBreached} onChange={(e) => { setFBreached(e.target.value); setPage(1); }} data-testid="filter-breached">
              <option value="all">— All —</option>
              <option value="true">Breached</option>
              <option value="false">Within SLA</option>
            </Select>
          </FilterField>
          <FilterField label="In Problem">
            <Select value={fProblem} onChange={(e) => { setFProblem(e.target.value); setPage(1); }} data-testid="filter-problem">
              <option value="all">— All —</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FilterField>
          <FilterField label="Created From">
            <TextInput type="date" value={fDateFrom} onChange={(e) => { setFDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
          </FilterField>
          <FilterField label="Created To">
            <TextInput type="date" value={fDateTo} onChange={(e) => { setFDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
          </FilterField>
        </div>
      )}

      <div className="bg-white border border-[#DDE5D8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0E2818] text-[#D4FF7A]">
            <tr>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Ticket #</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Type</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Customer</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Technician</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Status</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">% Completion</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Priority</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">SLA</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Age</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((t) => {
              const sla = timeLeft(t.sla_deadline_at);
              const isUrgent = t.priority === "urgent" || sla.overdue;
              const pct = ticketCompletionPct(t);
              const { done, total } = ticketActivityCounts(t);
              return (
                <tr key={t.id} data-testid={`service-ticket-row-${t.id}`} className={"border-b border-[#EEF2EA] hover:bg-[#F4F6F1] " + (isUrgent ? "bg-red-50/40" : "")}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0F1A12]">{t.ticket_number}</td>
                  <td className="px-4 py-3"><StatusPill value={TICKET_TYPE_LABEL[t.type] || t.type.replace("_", " ")} tone="info" /></td>
                  <td className="px-4 py-3 text-xs text-[#0F1A12] truncate max-w-[180px]" title={t.customer_snapshot?.customer_name}>{t.customer_snapshot?.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-[#4A5568]">{techName[t.technician_id] || "—"}</td>
                  <td className="px-4 py-3"><StatusPill value={t.status.replace(/_/g, " ")} tone={STATUS_TONE[t.status] || "muted"} /></td>
                  <td className="px-4 py-3" data-testid={`ticket-completion-${t.id}`}><CompletionBar pct={pct} done={done} total={total} /></td>
                  <td className="px-4 py-3">
                    {isUrgent ? (
                      <span data-testid={`ticket-urgent-${t.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3" /> Urgent
                      </span>
                    ) : (
                      <StatusPill value="Normal" tone="muted" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={"inline-flex items-center gap-1 text-[11px] font-mono " + (sla.overdue ? "text-red-700 font-semibold" : (sla.urgent ? "text-amber-700" : "text-[#475569]"))} data-testid={`ticket-sla-${t.id}`}>
                      <Clock className="w-3 h-3" /> {sla.text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#475569]">
                      <CalendarDays className="w-3 h-3" /> {ageDays(t.created_at)}d
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setDetailTicket(t)} data-testid={`service-ticket-open-${t.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#DDE5D8] hover:border-[#1F4E2C] hover:text-[#1F4E2C]">
                      Open <ChevronRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">
                {tickets.length === 0 ? "No tickets assigned to you yet." : "No tickets match the filters."}
              </td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length}
          onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} testid="tickets-pagination" />
      </div>

      <TicketDetailModal
        open={!!detailTicket}
        ticket={detailTicket}
        technicians={technicians}
        onClose={() => setDetailTicket(null)}
        onReload={() => { reload(); }}
      />
    </div>
  );
}

function TicketDetailModal({ open, ticket, technicians, onClose, onReload }) {
  const [local, setLocal] = useState(ticket);
  useEffect(() => { setLocal(ticket); }, [ticket]);
  if (!open || !local) return null;

  async function updateStatus(newStatus) {
    try {
      const r = await api.put(`/service-tickets/${local.id}/status?status=${newStatus}`);
      setLocal(r.data); onReload();
      toast.success(`Status → ${newStatus.replace(/_/g, " ")}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  }

  async function updateActivity(aid, patch) {
    try {
      const r = await api.put(`/service-tickets/${local.id}/activities/${aid}`, patch);
      setLocal(r.data); onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  }

  const sla = timeLeft(local.sla_deadline_at);
  const isUrgent = local.priority === "urgent" || sla.overdue;
  const pct = ticketCompletionPct(local);

  return (
    <Modal open={open} onClose={onClose} title={`${local.ticket_number} · ${local.title}`} wide>
      <div className="space-y-5" data-testid="service-ticket-detail">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill value={TICKET_TYPE_LABEL[local.type] || local.type.replace("_", " ")} tone="info" />
          <StatusPill value={local.status.replace(/_/g, " ")} tone={STATUS_TONE[local.status]} />
          {isUrgent && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5">
              <AlertTriangle className="w-3 h-3" /> Urgent
            </span>
          )}
          <span className={"inline-flex items-center gap-1 text-[11px] font-mono " + (sla.overdue ? "text-red-700 font-semibold" : "text-[#475569]")}>
            <Clock className="w-3 h-3" /> SLA: {sla.text}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#1F4E2C] font-semibold" data-testid="ticket-detail-completion">
            {pct}% complete
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 bg-[#F4F6F1] p-4 border border-[#DDE5D8] text-xs font-mono text-[#0F1A12]">
          <div><span className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Customer</span><div className="mt-0.5">{local.customer_snapshot?.customer_name}</div></div>
          <div><span className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Phone</span><div className="mt-0.5">{local.customer_snapshot?.phone}</div></div>
          <div className="col-span-2"><span className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Address</span><div className="mt-0.5">{local.customer_snapshot?.address || "—"}</div></div>
        </div>

        <Field label="Update Status">
          <Select value={local.status} onChange={(e) => updateStatus(e.target.value)} data-testid="ticket-status-select">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>

        {/* Activities (mirrored from opportunity) */}
        {local.activities?.length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C] mb-2">
              Phase 4 Activities · {local.activities.filter((a) => a.status === "completed").length} / {local.activities.length} done · {pct}%
            </div>
            <div className="border border-[#DDE5D8] divide-y divide-[#EEF2EA]">
              {local.activities.map((a) => (
                <div key={a.id} className="px-3 py-2.5 grid grid-cols-12 gap-2 items-center" data-testid={`ticket-activity-${a.id}`}>
                  <div className="col-span-5 flex items-center gap-2">
                    {a.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-[#1F4E2C]" /> :
                     a.status === "in_process" ? <Loader2 className="w-4 h-4 text-amber-600 animate-spin" /> :
                     <Circle className="w-4 h-4 text-[#94A3B8]" />}
                    <span className="text-xs text-[#0F1A12]">{a.name}</span>
                  </div>
                  <div className="col-span-3">
                    <Select value={a.status} onChange={(e) => updateActivity(a.id, { status: e.target.value })} data-testid={`activity-status-${a.id}`}>
                      <option value="pending">Pending</option>
                      <option value="in_process">In Process</option>
                      <option value="completed">Completed</option>
                      <option value="skipped">Skipped</option>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Select value={a.technician_id || ""} onChange={(e) => updateActivity(a.id, { technician_id: e.target.value })} data-testid={`activity-tech-${a.id}`}>
                      <option value="">— Assign technician —</option>
                      {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
